import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useInitializationDataContext } from '../../../context/data-context';
import {
  AddressTypes,
  DigitalAddress,
  EmailAddressForm as EmailAddressFormData,
  ErrorResponse,
} from '../../../global-types';
import { useSystemErrorHandling } from '../../../hooks';
import { recipientsQueryOptions, saveRecipient, updateRecipient } from '../../../services/recipients';
import { useAddressStore } from '../../../stores/address';
import { getFieldsFromAddressResponse } from '../../../utils';
import { trimObjectStrings } from '../../../utils/trim-object-strings';

export const useAddressEmailSubmit = () => {
  // Contexts
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const { addressData, updateDigitalAddress } = useAddressStore(
    useShallow((store) => ({
      addressData: store.addressData,
      updateDigitalAddress: store.updateDigitalAddress,
    })),
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = initializedData?.project_id ?? '';
  const recipients = useQuery({
    ...recipientsQueryOptions(projectId),
    enabled: !!projectId,
  });
  const submitting = useRef(false);

  // Error handler to show dialog when error is catch
  const { onSystemError } = useSystemErrorHandling();
  const saveEmailRecipient = useMutation<string, ErrorResponse, EmailAddressFormData>({
    retry: false,
    mutationFn: async (data: EmailAddressFormData) => {
      const response = await queryClient.fetchQuery(recipientsQueryOptions(projectId));
      if (response.data !== null && !Array.isArray(response.data)) {
        throw new Error('Recipient response is incomplete');
      }
      const currentRecipient = response.data?.[0];
      if (currentRecipient) {
        const recipientId = currentRecipient.recipient_id;
        if (!recipientId) throw new Error('Recipient response is missing its ID');
        await updateRecipient(projectId, recipientId, data);
        return recipientId;
      }
      const saved = await saveRecipient(projectId, data);
      const recipientId = saved.data?.recipient_id;
      if (!recipientId) throw new Error('Recipient response is missing its ID');
      return recipientId;
    },
    onSuccess: (recipientId, data) => {
      sessionStorage.setItem('recipient_id', recipientId);
      updateDigitalAddress(data);
      navigate({ pathname: '/card/customization/preview' });
    },
    onError: (error) => onSystemError(error),
    onSettled: () => queryClient.invalidateQueries({ queryKey: recipientsQueryOptions(projectId).queryKey }),
  });

  // Set the default address values to pre-populate the email form (if an address had been stored to the project)
  const emailFormDefaultData = useMemo(() => {
    const storedAddress = getFieldsFromAddressResponse(addressData[AddressTypes.DIGITAL] as DigitalAddress);
    return { ...storedAddress };
  }, [addressData.digital]);

  const emailValues = useForm<EmailAddressFormData>({
    mode: 'onBlur',
    defaultValues: emailFormDefaultData,
  });

  const {
    handleSubmit: handleEmailFormSubmit,
    formState: { isValid: isValidEmail },
    getValues: getValuesEmailForm,
  } = emailValues;

  useEffect(() => {
    if (recipients.isError) onSystemError(recipients.error);
  }, [recipients.isError, recipients.error, onSystemError]);

  const handleEmailSubmit = async (data: EmailAddressFormData) => {
    if (!projectId || recipients.isFetching || submitting.current) return;
    data = trimObjectStrings(data) as EmailAddressFormData;

    data.recipient_type_code = 'E';

    submitting.current = true;
    try {
      // onError preserves the existing system-error dialog without rejecting the form handler.
      await saveEmailRecipient.mutateAsync(data).catch(() => undefined);
    } finally {
      submitting.current = false;
    }
  };

  return {
    emailValues,
    handleEmailFormSubmit,
    isValidEmail,
    getValuesEmailForm,
    handleEmailSubmit,
    isLoading: recipients.isFetching || saveEmailRecipient.isPending,
  };
};
